import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

export default function ImageLightbox({ images = [], startIndex = 0, open, onClose, title = "" }) {
  const [i, setI] = useState(startIndex);
  useEffect(() => { if (open) setI(startIndex); }, [open, startIndex]);

  const go = useCallback((d) => setI((v) => (v + d + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, go, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && images.length > 0 && (
        <motion.div
          data-testid="merch-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${title} photos`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ pointerEvents: "auto" }}
          className="fixed inset-0 z-[100] bg-[#0C2E2B]/95 backdrop-blur-md flex flex-col"
          onClick={onClose}
        >
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-black text-lg truncate">{title} <span className="text-white/50 text-sm font-bold ml-2" data-testid="merch-lightbox-counter">{i + 1} / {images.length}</span></div>
            <button data-testid="merch-lightbox-close" onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex-1 relative flex items-center justify-center px-4" onClick={(e) => e.stopPropagation()}>
            {images.length > 1 && (
              <button data-testid="merch-lightbox-prev" onClick={() => go(-1)} className="absolute left-3 sm:left-6 w-12 h-12 rounded-full bg-white/10 hover:bg-[#FFDF00] hover:text-[#0C2E2B] text-white grid place-items-center transition-colors"><ChevronLeft className="w-6 h-6" /></button>
            )}
            <AnimatePresence mode="wait">
              <motion.img
                key={images[i]}
                data-testid="merch-lightbox-image"
                src={images[i]}
                alt={`${title} ${i + 1}`}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.04 }}
                transition={{ duration: 0.25 }}
                className="max-h-[70vh] max-w-full object-contain rounded-lg shadow-2xl select-none"
                draggable={false}
              />
            </AnimatePresence>
            {images.length > 1 && (
              <button data-testid="merch-lightbox-next" onClick={() => go(1)} className="absolute right-3 sm:right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-[#FFDF00] hover:text-[#0C2E2B] text-white grid place-items-center transition-colors"><ChevronRight className="w-6 h-6" /></button>
            )}
          </div>

          {images.length > 1 && (
            <div className="flex gap-2 justify-center px-4 py-4 overflow-x-auto" onClick={(e) => e.stopPropagation()} data-testid="merch-lightbox-thumbs">
              {images.map((u, idx) => (
                <button key={u} data-testid={`merch-lightbox-thumb-${idx}`} onClick={() => setI(idx)} className={`shrink-0 rounded-md overflow-hidden border-2 transition-colors ${idx === i ? "border-[#FFDF00]" : "border-white/20 hover:border-white/60"}`}>
                  <img src={u} alt="" className="w-16 h-16 object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export function ZoomHint() {
  return (
    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded bg-[#0C2E2B]/80 text-white pointer-events-none">
      <ZoomIn className="w-3 h-3" /> Tap to view full size
    </span>
  );
}
